/**
 * CSP-2788 (T9, AFU v2 r.271-281, R26-R32) - regole pure del luogo evento guidato: quando proporre i
 * candidati Google, come un candidato o un comune ISTAT diventano i campi del sinistro, e come si
 * riconosce il 422 di os-sinistri sul luogo incompleto (rami 6/8/9).
 */
import { corpoDaErrore, messaggioDelBe, statoHttpDaErrore } from '../../../../utils/esitoHttp';

/** Il contratto della nazione e' ISO2; le altre forme sono difesa sui dati storici (come LuogoEventoObbligoUtils). */
const FORME_ITALIA = ['IT', 'ITA', 'ITALIA'];

export const NAZIONE_ITALIA = 'IT';

/** Sotto i 2 caratteri il BE risponde 400: la ricerca non parte. */
export const MIN_CARATTERI_COMUNE = 2;

/** Massimo dei comuni chiesti al BE (default BE 20, massimo 50). */
export const MAX_COMUNI = 20;

/** Inizio del messaggio di LuogoEventoObbligoUtils.messaggioCampiMancanti (os-sinistri). */
const PREFISSO_LUOGO_INCOMPLETO = 'Luogo evento incompleto';
const SEPARATORE_CAMPI_MANCANTI = 'Campi non valorizzati:';
/** Forma del BFF all'esitazione (LuogoEventoAperturaGuard): "Luogo evento incompleto: completare comuneEvento, ...". */
const SEPARATORE_CAMPI_BFF = `${PREFISSO_LUOGO_INCOMPLETO}: completare `;

/**
 * Etichette di LuogoEventoObbligoUtils.CampoLuogoEvento -> nome del campo nel form. "via" e "cap" da CSP-2788 R28/R31
 * (via e CAP obbligatori sui rami 6/8/9, CAP del comune): contratto col BE, chiavi viaEvento e capEvento.
 */
const CAMPI_DA_ETICHETTA = {
  'luogo evento': 'luogoEvento',
  nazione: 'nazioneEvento',
  regione: 'regioneEvento',
  provincia: 'provinciaEvento',
  comune: 'comuneEvento',
  cap: 'capEvento',
  via: 'viaEvento',
};

/** Etichetta del BE o chiave del campo (in minuscolo, come le manda il BFF) -> nome del campo nel form. */
const CAMPO_DA_VOCE = Object.freeze({
  ...CAMPI_DA_ETICHETTA,
  ...Object.fromEntries(Object.values(CAMPI_DA_ETICHETTA).map((campo) => [campo.toLowerCase(), campo])),
});

/** Rami su cui il luogo evento e' obbligatorio per intero (RamoUtils.RAMI_CATASTROFALI di os-sinistri). */
const RAMI_CATASTROFALI = Object.freeze(['0006', '0008', '0009']);

const testo = (valore) => `${valore ?? ''}`.trim();

const valore = (v) => (v === null || v === undefined ? '' : `${v}`);

/**
 * Nazione assente = Italia, come in os-sinistri: il BE pretende i dettagli italiani anche sul
 * sinistro senza nazione.
 * @param {string} nazione nazione del luogo evento
 * @returns {boolean} true se il luogo va trattato come italiano
 */
export const isItalia = (nazione) => {
  const pulita = testo(nazione).toUpperCase();
  return !pulita || FORME_ITALIA.includes(pulita);
};

/**
 * R26: i candidati si propongono quando c'e' un luogo scritto ma non il comune, che e' il dettaglio
 * minimo preteso dal BE (decisione n.19). Con la descrizione libera non si propone nulla.
 * @param {object} dati dati denuncia correnti
 * @returns {boolean} true se al blur vanno chiesti i candidati
 */
export const isCandidatiDaProporre = (dati = {}) => Boolean(
  testo(dati.luogoEvento)
  && !testo(dati.comuneEvento)
  && dati.luogoEventoNonIndirizzabile !== true,
);

/**
 * Un candidato (LuogoEventoCandidato di os-sinistri) diventa il luogo evento: la stringa e' il
 * formatted_address, che il BE rinormalizza al salvataggio con la stessa chiamata Google; i dettagli
 * mancanti viaggiano come stringa vuota, perche' il candidato sostituisce il luogo per intero
 * (un null sarebbe scartato dal BFF e lascerebbe il valore memorizzato, OSSQ-2373).
 * @param {object} candidato {indirizzoFormattato, dettagli}
 * @returns {object} i campi del luogo evento da applicare
 */
export const campiDaCandidato = (candidato = {}) => {
  const dettagli = candidato.dettagli || {};
  return {
    luogoEvento: valore(candidato.indirizzoFormattato),
    viaEvento: valore(dettagli.via),
    nCivicoEvento: valore(dettagli.nCivico ?? dettagli.ncivico),
    capEvento: valore(dettagli.cap),
    comuneEvento: valore(dettagli.comune),
    provinciaEvento: valore(dettagli.provincia),
    regioneEvento: valore(dettagli.regione),
    nazioneEvento: valore(dettagli.nazione),
    latitudineEvento: dettagli.latitudine ?? null,
    longitudineEvento: dettagli.longitudine ?? null,
    luogoEventoNonIndirizzabile: false,
  };
};

/**
 * CAP del comune (fonte GeoNames, T7). Il campo e' opzionale: assente o vuoto = CAP a mano.
 * @param {object} comune ComuneItaliano
 * @returns {string[]} CAP distinti, nell'ordine del BE
 */
export const capDelComune = (comune) => (
  Array.isArray(comune?.cap)
    ? [...new Set(comune.cap.map(testo).filter(Boolean))]
    : []
);

const stessoComune = (a, b) => testo(a).toLowerCase() === testo(b).toLowerCase();

/**
 * R30/R31 (V6): scelto un comune ISTAT si compilano provincia (sigla) e regione (nome) e la nazione;
 * il CAP si compila se il comune ne ha uno solo, con piu' CAP si sceglie dalla lista (si tiene quello
 * attuale solo se e' fra quelli del comune), senza CAP si scrive a mano. Il CAP digitato per lo
 * stesso comune non si perde riscegliendolo.
 * @param {object} comune ComuneItaliano scelto
 * @param {object} attuali dati denuncia correnti (comuneEvento, capEvento)
 * @returns {object} i campi del luogo evento da applicare
 */
const capPerComune = (comune, attuali) => {
  const cap = capDelComune(comune);
  const capAttuale = testo(attuali.capEvento);
  if (cap.length === 1) return cap[0];
  if (cap.length > 1) return cap.includes(capAttuale) ? capAttuale : '';
  return stessoComune(comune?.comune, attuali.comuneEvento) ? capAttuale : '';
};

export const campiDaComune = (comune, attuali = {}) => ({
  comuneEvento: valore(comune?.comune),
  provinciaEvento: valore(comune?.siglaProvincia),
  regioneEvento: valore(comune?.regione),
  nazioneEvento: NAZIONE_ITALIA,
  capEvento: capPerComune(comune, attuali),
});

/** Comune svuotato in Italia: provincia, regione e CAP ne derivano e vanno con lui. */
export const campiComuneSvuotato = () => ({
  comuneEvento: '',
  provinciaEvento: '',
  regioneEvento: '',
  capEvento: '',
});

const elencoCampiMancanti = (messaggio) => {
  if (messaggio.includes(SEPARATORE_CAMPI_MANCANTI)) return messaggio.split(SEPARATORE_CAMPI_MANCANTI)[1];
  if (messaggio.startsWith(SEPARATORE_CAMPI_BFF)) return messaggio.slice(SEPARATORE_CAMPI_BFF.length);
  return '';
};

const campiMancanti = (messaggio) => {
  const campi = elencoCampiMancanti(messaggio)
    .split(',')
    .map((voce) => CAMPO_DA_VOCE[voce.trim().toLowerCase()])
    .filter(Boolean);
  return [...new Set(campi)];
};

/**
 * CSP-2788 R28 - ramo catastrofale (6, 8 o 9), in qualunque forma arrivi ("0006", "6", 6).
 * @param {string|number} ramo codice ramo
 */
export const isRamoCatastrofale = (ramo) => {
  const cifre = testo(ramo);
  return /^\d{1,4}$/.test(cifre) && RAMI_CATASTROFALI.includes(cifre.padStart(4, '0'));
};

/**
 * CSP-2788 R28 - sui rami 6/8/9 il luogo evento va indicato per intero (via e CAP compresi): basta un danneggiato su
 * uno di quei rami, come in LuogoEventoObbligoUtils, o il ramo scelto nel task prima che il danneggiato esista.
 * @param {Array<{ramoGaranzia: string}>} danneggiati listaDanneggiati del task
 * @param {string} [ramoTask] openedTicket.ramoGaranziaDanneggiato
 */
export const isLuogoEventoObbligatorio = (danneggiati, ramoTask) =>
  isRamoCatastrofale(ramoTask)
  || (Array.isArray(danneggiati) && danneggiati.some((danneggiato) => isRamoCatastrofale(danneggiato?.ramoGaranzia)));

/**
 * R32: riconosce il rifiuto di os-sinistri per luogo incompleto sui rami 6/8/9 (422 al salvataggio;
 * lo stesso testo arriva come 400 dalla verifica all'esitazione, R33) e ne ricava i campi mancanti.
 * <p>
 * Il PUT della denuncia passa da axiosInstance, il cui interceptor rigetta col SOLO body del BE: lo
 * status si ricava dal body ExceptionObject (9422 -> 422) con statoHttpDaErrore, che accetta anche un
 * errore axios completo. Il BFF inoltra il body di os-sinistri a volte come stringa JSON.
 * @param {*} errore body rigettato dall'interceptor, oppure errore axios
 * @returns {{messaggio: string, campi: string[]} | null} null se l'errore non riguarda il luogo
 */
export const leggiErroreLuogoEvento = (errore) => {
  const status = statoHttpDaErrore(errore);
  if (status !== 422 && status !== 400) return null;
  const messaggio = messaggioDelBe(corpoDaErrore(errore));
  if (!messaggio?.startsWith(PREFISSO_LUOGO_INCOMPLETO)) return null;
  return { messaggio, campi: campiMancanti(messaggio) };
};
